import { toast } from "sonner";
import { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";

import { EventLevelBadge } from "../../components/status-badge";
import { JsonBlock } from "../../components/json-block";
import { formatDateTime, formatTokens, formatUsd } from "../../lib/format";
import type { PipelineEvent, PipelineRunDetail } from "../../lib/api";

type EventContext = Pick<
  PipelineRunDetail,
  | "batch"
  | "channelId"
  | "delivery"
  | "inboundText"
  | "llmRequest"
  | "llmRequestCapturedAt"
  | "llmResponse"
  | "llmRouting"
  | "llmUsage"
  | "sender"
>;

const checkLabels: Record<string, string> = {
  escalation: "Передача менеджеру",
  knowledge: "База знаний",
  crmWrites: "Изменение полей CRM",
  outcomes: "Цель и квалификация",
  followups: "Повторные касания",
  account: "Аккаунт активен",
  dialogLimit: "Лимит диалогов доступен",
  amo: "amoCRM подключена",
  wazzup: "Wazzup подключён",
  funnel: "Агент активен",
  channelBinding: "Канал привязан к агенту",
  leadStatus: "Этап сделки разрешён",
};

const checkResultCopy: Record<string, string> = {
  PASSED: "Проверка пройдена",
  FAILED: "Проверка не пройдена",
  NOT_APPLICABLE: "Проверка не требуется для этого запуска",
};

function detailString(details: unknown, key: string): string | null {
  if (!details || typeof details !== "object" || Array.isArray(details))
    return null;
  const value = (details as Record<string, unknown>)[key];
  return typeof value === "string" ? value : null;
}

function detailScalar(details: unknown, key: string): string | null {
  if (!details || typeof details !== "object" || Array.isArray(details))
    return null;
  const value = (details as Record<string, unknown>)[key];
  return typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
    ? String(value)
    : null;
}

async function copyText(content: string) {
  try {
    await navigator.clipboard.writeText(content);
    toast.success("Скопировано в буфер обмена");
  } catch {
    toast.error("Не удалось скопировать. Выделите текст и скопируйте вручную.");
  }
}

function TextAttachment({
  label,
  content,
}: {
  label: string;
  content: string;
}) {
  return (
    <div>
      <p className="mb-1 text-xs font-medium text-muted-foreground">{label}</p>
      <pre className="whitespace-pre-wrap break-words rounded-md border bg-muted/40 p-3 font-sans text-sm">
        {content}
      </pre>
      <button
        type="button"
        className="mt-2 text-xs font-medium text-primary hover:underline"
        onClick={() => copyText(content)}
      >
        Копировать
      </button>
    </div>
  );
}

function FactGrid({
  facts,
}: {
  facts: Array<{ label: string; value: string | null }>;
}) {
  const visibleFacts = facts.filter(
    (fact) => fact.value !== null && fact.value !== "",
  );
  if (visibleFacts.length === 0) return null;
  return (
    <dl className="grid gap-2 rounded-md border bg-muted/40 p-3 text-sm sm:grid-cols-2">
      {visibleFacts.map((fact) => (
        <div key={fact.label}>
          <dt className="text-xs text-muted-foreground">{fact.label}</dt>
          <dd className="break-all font-medium">{fact.value}</dd>
        </div>
      ))}
    </dl>
  );
}

function CheckList({ details }: { details: unknown }) {
  if (!details || typeof details !== "object" || Array.isArray(details))
    return null;
  const checks = (details as Record<string, unknown>).checks;
  if (!checks || typeof checks !== "object" || Array.isArray(checks))
    return null;
  return (
    <div className="space-y-2">
      {Object.entries(checks).map(([key, raw]) => {
        if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
        const check = raw as Record<string, unknown>;
        const result =
          typeof check.result === "string" ? check.result : "UNKNOWN";
        const explanation =
          typeof check.explanation === "string"
            ? check.explanation
            : (checkResultCopy[result] ?? "Результат проверки неизвестен");
        const facts = Object.entries(check)
          .filter(
            ([name, value]) =>
              name !== "result" &&
              name !== "explanation" &&
              (typeof value === "string" ||
                typeof value === "number" ||
                typeof value === "boolean"),
          )
          .map(([name, value]) => `${name}: ${String(value)}`)
          .join(", ");
        return (
          <div key={key} className="rounded-md border bg-muted/40 p-3 text-sm">
            <p className="font-medium">{checkLabels[key] ?? key}</p>
            <p className="text-muted-foreground">
              {explanation}
              {facts ? ` · ${facts}` : ""}
            </p>
          </div>
        );
      })}
    </div>
  );
}

function EventAttachment({
  event,
  context,
}: {
  event: PipelineEvent;
  context: EventContext;
}) {
  // A followup has its own LLM snapshot, supplied in this event's attachments.
  if (detailString(event.details, "generationId")) return null;
  if (
    event.step === "INBOUND_MESSAGE_RECEIVED" ||
    event.step === "INBOUND_MESSAGE_UNSUPPORTED"
  ) {
    const inboundId = detailString(event.details, "inboundMessageId");
    const inbound = context.batch?.messages.find(
      (message) => message.id === inboundId,
    );
    return inbound ? (
      <div className="space-y-3">
        <TextAttachment
          label={`Конкретная входящая реплика · ${inbound.senderName ?? inbound.senderPhone ?? "отправитель не указан"}`}
          content={
            inbound.text ??
            `Неподдерживаемый тип: ${inbound.messageType ?? "неизвестен"}`
          }
        />
        <FactGrid
          facts={[
            { label: "Имя", value: inbound.senderName },
            { label: "Телефон", value: inbound.senderPhone },
            { label: "Username", value: inbound.senderUsername },
            { label: "Chat ID", value: context.sender.chatId },
            { label: "Тип чата", value: context.sender.chatType },
            { label: "Тип сообщения", value: inbound.messageType },
            { label: "Channel ID", value: context.channelId },
            { label: "Wazzup message ID", value: inbound.wazzupMessageId },
            {
              label: "Время Wazzup",
              value: inbound.sourceDateTime
                ? formatDateTime(inbound.sourceDateTime)
                : null,
            },
            {
              label: "Получено сервером",
              value: formatDateTime(inbound.receivedAt),
            },
          ]}
        />
      </div>
    ) : null;
  }
  if (event.step === "DEBOUNCE_COMPLETED") {
    return (
      <TextAttachment
        label="Итоговый текст пакета"
        content={context.inboundText}
      />
    );
  }
  if (event.step === "LLM_CONTEXT_BUILT") {
    return context.llmRequest ? (
      <div className="space-y-2">
        <FactGrid
          facts={[
            {
              label: "Контекст сохранён",
              value: context.llmRequest.capturedAt
                ? formatDateTime(context.llmRequest.capturedAt)
                : null,
            },
            {
              label: "Сообщений в контексте",
              value: String(context.llmRequest.messages.length),
            },
          ]}
        />
        {context.llmRequest.messages.map((message, index) => (
          <TextAttachment
            key={`${message.role}-${index}`}
            label={`${index + 1}. ${message.role}`}
            content={message.content}
          />
        ))}
      </div>
    ) : (
      <p className="text-sm text-muted-foreground">
        {context.llmRequestCapturedAt
          ? "Содержимое удалено по сроку хранения."
          : "Вызов AI не начинался или исторический snapshot отсутствует."}
      </p>
    );
  }
  if (event.step === "LLM_ATTEMPT_STARTED") {
    return (
      <FactGrid
        facts={[
          {
            label: "Провайдер",
            value: detailString(event.details, "provider"),
          },
          { label: "Модель", value: detailString(event.details, "model") },
          {
            label: "Номер попытки",
            value: detailScalar(event.details, "attempt"),
          },
        ]}
      />
    );
  }
  if (event.step === "LLM_RESPONSE_PERSISTED") {
    return context.llmResponse ? (
      <div className="space-y-3">
        <TextAttachment
          label="Точный ответ AI"
          content={context.llmResponse.content}
        />
        <FactGrid
          facts={[
            { label: "Провайдер", value: context.llmResponse.provider },
            { label: "Модель", value: context.llmResponse.model },
            {
              label: "Задержка",
              value:
                context.llmResponse.latencyMs === null
                  ? null
                  : `${context.llmResponse.latencyMs} мс`,
            },
            {
              label: "Всего токенов",
              value: formatTokens(context.llmUsage.totalTokens),
            },
            {
              label: "Токены промпта",
              value: formatTokens(context.llmUsage.promptTokens),
            },
            {
              label: "Токены ответа",
              value: formatTokens(context.llmUsage.completionTokens),
            },
            { label: "Стоимость", value: formatUsd(context.llmUsage.costUsd) },
            {
              label: "Внешних попыток",
              value: String(context.llmRouting.attempts),
            },
            {
              label: "Fallback",
              value: context.llmRouting.fallbackUsed
                ? "использован"
                : "не использован",
            },
          ]}
        />
      </div>
    ) : (
      <p className="text-sm text-muted-foreground">
        Сохранённого ответа AI нет.
      </p>
    );
  }
  if (event.step === "WAZZUP_SEND_STARTED") {
    return (
      <div className="space-y-3">
        {context.llmResponse ? (
          <TextAttachment
            label="Точный текст для Wazzup"
            content={context.llmResponse.content}
          />
        ) : (
          <p className="text-sm text-muted-foreground">
            Текст ответа недоступен.
          </p>
        )}
        <FactGrid
          facts={[
            { label: "Получатель (Chat ID)", value: context.sender.chatId },
            { label: "Тип чата", value: context.sender.chatType },
            { label: "Channel ID", value: context.channelId },
          ]}
        />
      </div>
    );
  }
  if (
    event.step === "WAZZUP_SEND_ACCEPTED" ||
    event.step === "WAZZUP_SEND_FAILED" ||
    event.step === "WAZZUP_SEND_UNKNOWN" ||
    event.step.startsWith("WAZZUP_DELIVERY_")
  ) {
    const latencyMs = detailScalar(event.details, "latencyMs");
    return (
      <div className="space-y-3">
        <FactGrid
          facts={[
            { label: "Статус доставки", value: context.delivery.display.label },
            {
              label: "Wazzup message ID",
              value:
                detailString(event.details, "messageId") ??
                context.delivery.wazzupMessageId,
            },
            {
              label: "HTTP-статус",
              value: detailScalar(event.details, "httpStatus"),
            },
            { label: "Задержка", value: latencyMs ? `${latencyMs} мс` : null },
            {
              label: "Статус события",
              value: detailScalar(event.details, "status"),
            },
            {
              label: "Применён к текущему статусу",
              value: detailScalar(event.details, "projectionApplied"),
            },
            {
              label: "Обновлено",
              value: context.delivery.updatedAt
                ? formatDateTime(context.delivery.updatedAt)
                : null,
            },
          ]}
        />
        <p className="text-sm text-muted-foreground">
          {context.delivery.display.description}
        </p>
        {context.delivery.error ? (
          <p className="rounded-md bg-danger/10 p-3 text-sm text-danger">
            {context.delivery.error.code ?? "Ошибка"}:{" "}
            {context.delivery.error.description ?? "без описания"}
          </p>
        ) : null}
      </div>
    );
  }
  return null;
}

function EventRow({
  event,
  context,
}: {
  event: PipelineEvent;
  context: EventContext;
}) {
  const [open, setOpen] = useState(event.level === "ERROR");
  return (
    <div className="relative pl-7">
      <span
        className={`absolute left-0 top-2 h-3 w-3 rounded-full border-2 bg-card ${
          event.level === "ERROR"
            ? "border-danger"
            : event.level === "WARN"
              ? "border-warning"
              : "border-primary"
        }`}
      />
      <div className="panel overflow-hidden">
        <div className="flex w-full items-start gap-3 p-4 text-left">
          <button
            className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md border bg-card"
            type="button"
            aria-label={open ? "Свернуть событие" : "Раскрыть событие"}
            aria-expanded={open}
            onClick={() => setOpen((value) => !value)}
          >
            {open ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </button>
          <span className="min-w-0 flex-1">
            <span className="flex flex-wrap items-center gap-2">
              <EventLevelBadge level={event.level} />
              <span className="text-xs text-muted-foreground">
                {formatDateTime(event.occurredAt)}
              </span>
              <span className="rounded-md bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                {event.step}
              </span>
            </span>
            <span className="mt-2 block font-medium">
              {event.display.title}
            </span>
            <span className="mt-1 block text-sm text-muted-foreground">
              {event.display.description}
            </span>
            {event.display.operatorHint ? (
              <span className="mt-2 block rounded-md bg-warning/10 p-2 text-sm text-warning-foreground">
                Что проверить: {event.display.operatorHint}
              </span>
            ) : null}
          </span>
        </div>
        {open ? (
          <div className="space-y-3 border-t p-4">
            {event.attachments?.map((attachment, index) => (
              <TextAttachment key={`${event.id}-${index}`} {...attachment} />
            ))}
            <EventAttachment event={event} context={context} />
            <CheckList details={event.details} />
            <JsonBlock value={event.details} />
            {event.raw !== null ? <JsonBlock value={event.raw} /> : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export function EventTimeline({
  events,
  context,
}: {
  events: PipelineEvent[];
  context: EventContext;
}) {
  return (
    <section>
      <h2 className="mb-3 text-lg font-semibold">Полная цепочка обработки</h2>
      <div className="relative space-y-3 before:absolute before:left-[5px] before:top-2 before:h-[calc(100%-1rem)] before:w-px before:bg-border">
        {events.map((event) => (
          <EventRow key={event.id} event={event} context={context} />
        ))}
      </div>
    </section>
  );
}
